// src/pages/pentest/dashboard/Cves.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress, // <-- added
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate, useParams } from 'react-router-dom';
import { alpha , darken, lighten} from '@mui/material/styles';
import { BASEURL ,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';
import Nav from './nav';
// (Removed Doughnut/Bar imports)

// ----- helpers -----
const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'informational'];
const normSev = (s) => (s ? String(s).toLowerCase() : 'unknown');
const sevBg = (sev) => {
  switch (normSev(sev)) {
    case 'critical': return '#9c27b0';
    case 'high': return '#f44336';
    case 'medium': return '#ff9800';
    case 'low': return '#2196f3';
    case 'informational': return '#4caf50';
    default: return '#9e9e9e';
  }
};

// CPE 2.3: cpe:2.3:<part>:<vendor>:<product>:<version>:...
const parseCpe = (cpe) => {
  if (!cpe || typeof cpe !== 'string') return { vendor: '—', product: '—', version: '—' };
  const parts = cpe.split(':');
  if (parts.length >= 6 && parts[0] === 'cpe' && parts[1] === '2.3') {
    return { vendor: parts[3] || '—', product: parts[4] || '—', version: parts[5] || '—' };
  }
  const [vendor, product = '—'] = cpe.split(':');
  return { vendor: vendor || '—', product, version: '—' };
};

// Mini progress-row like on the API Exploits page
const NiceBarRow = ({ label, value = 0, max = 1, onClick }) => {
  const pct = Math.min(100, Math.round((value / Math.max(1, max)) * 100));
  return (
    <Stack spacing={0.5} sx={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 600 }} title={label}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{value}</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 999 }} />
    </Stack>
  );
};

const Cves = () => {
  const [cveData, setCveData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCategory, setSearchCategory] = useState('any'); // any | cve | description | cpe | asset | severity
  const [expandedDesc, setExpandedDesc] = useState({}); // row.id -> bool
  const navigate = useNavigate();
  const { pentest_id } = useParams();

  const getCves = (token) => {
    fetch(`${BASEURL}/pentest/cves?pentest_id=${pentest_id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.error && data?.message === 'Invalid token') navigate('/');
        else setCveData(Array.isArray(data?.cves) ? data.cves : []);
      })
      .catch((err) => console.error('Error fetching CVEs:', err));
  };

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) getCves(token);
    else {
      console.error('No token found');
      navigate('/');
    }
  }, [navigate, pentest_id]);

  // Enrich rows
  const rows = useMemo(() => {
    return cveData.map((r, i) => {
      const sev = normSev(r.severity);
      const assets = Array.isArray(r.vulnerable_assets) ? r.vulnerable_assets : [];
      const cpeStr = r.cpe || '';
      const { vendor, product, version } = parseCpe(cpeStr);
      return {
        id: i + '-' + (r.cve_id || 'cve'),
        cve: r.cve_id || 'N/A',
        severity: sev,
        description: r.description || '',
        cpe: cpeStr,
        vendor, product, version,
        assets,
      };
    });
  }, [cveData]);

  // KPIs (kept; used for counts if needed elsewhere)
  const kpis = useMemo(() => {
    const total = rows.length;
    const counts = { critical: 0, high: 0, medium: 0, low: 0, informational: 0, unknown: 0 };
    const assetSet = new Set();
    rows.forEach((r) => {
      counts[r.severity] = (counts[r.severity] || 0) + 1;
      r.assets.forEach((a) => assetSet.add(a));
    });
    return { total, ...counts, uniqueAssets: assetSet.size };
  }, [rows]);

  // New: severity card counts (critical/high/medium/low/info) like Exploits
  const sevCounts = useMemo(() => {
    const out = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    rows.forEach((r) => {
      const s = r.severity;
      if (s === 'informational') out.info++;
      else if (out[s] !== undefined) out[s]++;
    });
    return out;
  }, [rows]);

  // New: Top 5 LOCATIONS (assets)
  const topLocations = useMemo(() => {
    const map = {};
    rows.forEach((r) => r.assets.forEach((a) => {
      const key = String(a || '').trim();
      if (!key) return;
      map[key] = (map[key] || 0) + 1;
    }));
    return Object.entries(map)
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [rows]);

  // New: Top 5 CPEs (use the full CPE string to ensure correctness)
  const topCPEs = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const key = String(r.cpe || '').trim();
      if (!key) return; // skip empty
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map)
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [rows]);

  const maxLoc = Math.max(1, ...topLocations.map((x) => x.value));
  const maxCpe = Math.max(1, ...topCPEs.map((x) => x.value));

  // Filtering (unchanged)
  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const q = searchTerm.toLowerCase();
    return rows.filter((r) => {
      if (searchCategory === 'cve') return r.cve.toLowerCase().includes(q);
      if (searchCategory === 'description') return r.description.toLowerCase().includes(q);
      if (searchCategory === 'cpe') return (r.cpe || '').toLowerCase().includes(q);
      if (searchCategory === 'asset') return r.assets.some((a) => String(a).toLowerCase().includes(q));
      if (searchCategory === 'severity') return r.severity.includes(q);
      return (
        r.cve.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        (r.cpe || '').toLowerCase().includes(q) ||
        r.assets.some((a) => String(a).toLowerCase().includes(q)) ||
        r.severity.includes(q)
      );
    });
  }, [rows, searchTerm, searchCategory]);

  const toggleDesc = (id) => setExpandedDesc((s) => ({ ...s, [id]: !s[id] }));

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      <Nav />

      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        {/* ---------- Swapped Header & KPI/Charts (API-style) ---------- */}
        <Stack spacing={2} sx={{ mb: 2 }}>
          {/* Header */}
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>CVEs</Typography>
              <Typography variant="body2" color="text.secondary">
                Vulnerabilities discovered for this pentest. Click a row to view details.
              </Typography>
            </Box>
          </Stack>

          {/* Severity cards */}
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            {['critical','high','medium','low','info'].map((s) => (
              <Card key={s} sx={{ p: 2, borderRadius: 3, boxShadow: 2, minWidth: 180 }}>
                <Typography variant="overline" color="text.secondary">{s.toUpperCase()}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" sx={{ bgcolor: sevBg(s === 'info' ? 'informational' : s), color:'#fff', fontWeight: 700 }} label={s} />
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{sevCounts[s]}</Typography>
                </Stack>
              </Card>
            ))}
          </Stack>

          {/* Mini charts: Top 5 Locations & Top 5 CPEs */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2, flex: 1 }}>
              <Typography variant="overline" color="text.secondary">TOP 5 LOCATIONS</Typography>
              <Stack spacing={1.25} sx={{ mt: 1 }}>
                {topLocations.length ? (
                  topLocations.map((row) => (
                    <NiceBarRow
                      key={row.key}
                      label={row.key}
                      value={row.value}
                      max={maxLoc}
                      onClick={() => setSearchTerm(row.key)}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">No data</Typography>
                )}
              </Stack>
            </Card>

            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2, flex: 1 }}>
              <Typography variant="overline" color="text.secondary">TOP 5 CPEs</Typography>
              <Stack spacing={1.25} sx={{ mt: 1 }}>
                {topCPEs.length ? (
                  topCPEs.map((row) => (
                    <NiceBarRow
                      key={row.key}
                      label={row.key}
                      value={row.value}
                      max={maxCpe}
                      onClick={() => { setSearchCategory('cpe'); setSearchTerm(row.key); }}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">No data</Typography>
                )}
              </Stack>
            </Card>
          </Stack>

          <Divider />
        </Stack>

        {/* ---------- Filters + Table (unchanged) ---------- */}
        <Card sx={{ p: 2, borderRadius: 3, mt: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                CVE Inventory
              </Typography>
              <Chip
                size="small"
                label={`${filteredRows.length}/${rows.length || 0} shown`}
                sx={{ ml: 1, fontWeight: 700 }}
                variant="outlined"
              />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Filter By</InputLabel>
                <Select value={searchCategory} label="Filter By" onChange={(e) => setSearchCategory(e.target.value)}>
                  <MenuItem value="any">Any</MenuItem>
                  <MenuItem value="cve">CVE</MenuItem>
                  <MenuItem value="description">Description</MenuItem>
                  <MenuItem value="cpe">CPE</MenuItem>
                  <MenuItem value="asset">Asset</MenuItem>
                  <MenuItem value="severity">Severity</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Search… (e.g., CVE-2024-xxxx, cpe:2.3:a:nginx:nginx, critical)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ ml: 1, minWidth: 280 }}
              />
              <Button variant="outlined" onClick={() => setSearchTerm('')}>Clear</Button>
            </Stack>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Table (unchanged) */}
          <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 6, overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1200 }}>
              <TableHead sx={{ backgroundColor: PRIMARYCOLOR }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 180 }}>CVE</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 120 }}>Severity</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 360 }}>Description</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 300 }}>CPE</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 340 }}>Locations</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, width: 100 }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredRows.length ? (
                  filteredRows
                    .sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity))
                    .map((row) => {
                      const isOpen = !!expandedDesc[row.id];
                      return (
                        <TableRow key={row.id} hover sx={{ '&:hover': { backgroundColor: 'action.hover' }, verticalAlign: 'top' }}>
                          {/* CVE */}
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <Typography sx={{ fontWeight: 700 }}>{row.cve}</Typography>
                              <Tooltip title="Copy CVE">
                                <IconButton
                                  size="small"
                                  onClick={async () => { try { await navigator.clipboard.writeText(row.cve); } catch {} }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                              {row.vendor !== '—' ? `${row.vendor}` : ''}{row.product !== '—' ? ` • ${row.product}` : ''}{row.version !== '—' ? ` • v${row.version}` : ''}
                            </Typography>
                          </TableCell>

                          {/* Severity */}
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.severity.toUpperCase()}
                              sx={{ bgcolor: sevBg(row.severity), color: '#fff', fontWeight: 800 }}
                            />
                          </TableCell>

                          {/* Description with show more/less */}
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: isOpen ? 'unset' : 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {row.description || '—'}
                            </Typography>
                            {row.description && row.description.length > 180 && (
                              <Button size="small" variant="text" onClick={() => toggleDesc(row.id)}>
                                {isOpen ? 'Show less' : 'Show more'}
                              </Button>
                            )}
                          </TableCell>

                          {/* CPE */}
                          <TableCell>
                            <Typography sx={{ fontWeight: 600 }} noWrap title={row.cpe || '—'}>
                              {row.cpe || '—'}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                              {row.vendor} {row.product !== '—' ? `• ${row.product}` : ''}{row.version !== '—' ? ` • v${row.version}` : ''}
                            </Typography>
                          </TableCell>

                          {/* Locations (assets) */}
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {row.assets?.length ? (
                                row.assets.map((a) => (
                                  <Chip key={a} label={a} size="small" sx={{ maxWidth: 200 }} />
                                ))
                              ) : (
                                <Typography variant="body2">—</Typography>
                              )}
                            </Box>
                          </TableCell>

                          {/* Actions */}
                          <TableCell>
                            <Tooltip title="Open on NVD">
                              <IconButton
                                size="small"
                                component="a"
                                href={`https://nvd.nist.gov/vuln/detail/${encodeURIComponent(row.cve)}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography>No CVE data available</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>
    </Box>
  );
};

export default Cves;
